import { ImViewBridge } from '../../legacy/ImViewBridge.js';
import { DomHelpers } from '../DomHelpers.js';
import { Widget } from '../Widget.js';
import { ImSys } from '../ImSys.js';
import { Loc } from '../../locales/registry.js';
import { BgSaveScope } from '../../view/BgSaveScope.js';

/*
 * 2026-09-14: единый lifecycle закрытия modal aside.
 * Закрытие крестиком (мышь, Enter/Space), Escape, командой closePopup и
 * ViewWidget.close() проходит через requestAsideClose: при несохранённых
 * данных view внутри панели запрашивается подтверждение.
 * Замена содержимого общей панели (openAside, карточка и вставка строки таблицы)
 * проходит ту же проверку через confirmAsideReplace.
 * Закрытие кликом вне панели сознательно не поддерживается; у фильтра
 * таблицы остаётся собственный closeOnOutsideClick в FilterWidget.
 *
 * 2026-10-15: панель с областью фонового сохранения ([data-bgsave-scope])
 * закрывается асинхронно: requestAsideClose синхронно переводит область в finishing
 * и возвращает false, затем дожидается очереди сохранений. При ошибке панель остаётся
 * открытой, при успехе закрывается через closeAside без повторного входа в проверку.
 * Повторный Escape или крестик во время ожидания игнорируются.
 *
 * 2026-10-15: единый выход из такой панели. Если в области были успешные записи,
 * крестик, Escape и ViewWidget.close() отправляют серверную команду кнопки завершения
 * ([data-wait-bgsave] внутри области; сервер отвечает RedirectDto). Без записей
 * (или без кнопки завершения) панель просто закрывается.
 * Замена содержимого (confirmAsideReplace) сначала завершает область и выполняется
 * только после успешной очереди; при ошибке вызывается onCancel, панель остаётся.
 *
 * 2026-10-15: закрытие без сохранения. Если предыдущее завершение области было неуспешным
 * (крестик, Escape или кнопка «Готово») и повторное тоже не удалось, показывается
 * подтверждение «Закрыть без сохранения?». Согласие ведёт к тому же выходу
 * (exitScopedAside): после успешных записей — команда завершения, иначе закрытие.
 */
/** Кнопка завершения области фонового сохранения */
const FINISH_BUTTON_SELECTOR = "[data-wait-bgsave]";
const ASIDE_SELECTOR = ".im-modal-aside";
const CLOSE_SELECTOR = ".modal-aside__close";
/** Общая панель форм (openAside, карточка строки); панель фильтра у каждой таблицы своя */
const SHARED_ASIDE_SELECTOR = ".im-modal-aside:not(.im-modal-aside-filter)";
/** Панели, к которым уже подключено закрытие (защита от накопления обработчиков) */
const wiredAsides = new WeakSet();
/** Панели, для которых сейчас открыт диалог подтверждения */
const pendingConfirm = new WeakSet();
let escapeInstalled = false;
function modalAside(root = document.body) {
    const asides = Array.from(root.querySelectorAll(ASIDE_SELECTOR));
    if (root.matches(ASIDE_SELECTOR))
        asides.unshift(root);
    asides.forEach(modal => {
        applyDialogTitle(modal);
        wireAside(modal);
    });
    installEscape();
}
/**
 * Запросить закрытие панели.
 * Если во view внутри панели есть несохранённые изменения, показывает подтверждение
 * и закрывает панель только после согласия.
 * @returns true, если панель закрыта сразу
 */
function requestAsideClose(aside) {
    if (!aside.isConnected || pendingConfirm.has(aside))
        return false;
    const scope = isHideMode(aside) ? null : BgSaveScope.within(aside);
    if (scope) {
        if (!BgSaveScope.isFinishing(scope))
            void closeScopedAside(aside, scope);
        return false;
    }
    if (!isHideMode(aside) && hasUnsavedAsideChanges(aside)) {
        confirmAsideClose(aside, () => closeAside(aside));
        return false;
    }
    closeAside(aside);
    return true;
}
/**
 * Показать подтверждение закрытия панели с несохранёнными данными.
 * Повторный вызов, пока диалог открыт, игнорируется.
 * @param onCancel отказ: кнопка «Отмена» либо закрытие диалога без кнопок
 */
function confirmAsideClose(aside, onConfirm, onCancel) {
    if (pendingConfirm.has(aside))
        return;
    pendingConfirm.add(aside);
    // Кнопка диалога вызывает свой обработчик, затем close() и onClose — отвечаем только один раз
    let settled = false;
    const settle = (action) => {
        if (settled)
            return;
        settled = true;
        pendingConfirm.delete(aside);
        action?.();
    };
    const dialog = ImSys.alertConfirm(Loc.confirmation, Loc.closeUnsavedTabConfirmation.replace(/\n/g, "<br/>"), () => settle(onConfirm), () => settle(onCancel), undefined, true, Loc.close);
    // Диалог может закрыться без кнопок (Escape, крестик диалога)
    dialog?.setOptions({ onClose: () => settle(onCancel) });
}
/**
 * 2026-09-14: перед заменой содержимого общей панели другой формой.
 * Без несохранённых данных onConfirm выполняется сразу, иначе — после подтверждения.
 * Пока диалог по этой панели уже открыт, новая замена отбрасывается без вызова колбэков.
 * 2026-10-15: панель с областью фонового сохранения сначала завершает очередь
 * (во время завершения новая замена тоже отбрасывается).
 */
function confirmAsideReplace(onConfirm, onCancel) {
    const aside = document.querySelector(SHARED_ASIDE_SELECTOR);
    if (!aside || !isShown(aside)) {
        onConfirm();
        return;
    }
    const scope = isHideMode(aside) ? null : BgSaveScope.within(aside);
    if (scope) {
        if (!BgSaveScope.isFinishing(scope) && !pendingConfirm.has(aside)) {
            void replaceScopedAside(aside, scope, onConfirm, onCancel);
        }
        return;
    }
    if (!hasUnsavedAsideChanges(aside)) {
        onConfirm();
        return;
    }
    confirmAsideClose(aside, onConfirm, onCancel);
}
/**
 * Есть ли несохранённые изменения во view внутри панели.
 * 2026-10-15: для view внутри области фонового сохранения учитываются только изменения,
 * которые не сохраняет её очередь (поля без bgSave и без серверной onchange-команды).
 */
function hasUnsavedAsideChanges(aside) {
    return Widget.getAll().some(w => {
        const view = w;
        if (typeof view.hasUnsavedChanges !== "function"
            || !view.element
            || !aside.contains(view.element)) {
            return false;
        }
        if (typeof view.hasUncoveredChanges === "function" && BgSaveScope.closest(view.element)) {
            return view.hasUncoveredChanges();
        }
        return view.hasUnsavedChanges();
    });
}
/**
 * 2026-10-15: закрытие панели с областью фонового сохранения после завершения очереди.
 * Первая ошибка сохранения оставляет панель открытой (область уже разблокирована),
 * повторная после неуспешного завершения — предлагает закрыть без сохранения.
 * Изменения, не покрытые фоновым сохранением, по-прежнему требуют подтверждения.
 */
async function closeScopedAside(aside, scope) {
    const failedBefore = BgSaveScope.hasFailedFinish(scope);
    const ok = await BgSaveScope.finish(scope);
    if (!aside.isConnected)
        return;
    if (!ok) {
        // Повторное закрытие после неуспешного завершения: выход с отказом от изменений
        if (failedBefore && !BgSaveScope.isFinishing(scope)) {
            confirmAsideClose(aside, () => exitScopedAside(aside, scope));
        }
        return;
    }
    if (hasUnsavedAsideChanges(aside)) {
        BgSaveScope.release(scope);
        confirmAsideClose(aside, () => exitScopedAside(aside, scope));
        return;
    }
    exitScopedAside(aside, scope);
}
/**
 * 2026-10-15: выход из панели с завершённой областью.
 * После успешных записей — серверная команда кнопки завершения (RedirectDto),
 * область заблокирована до ответа (в том числе при выходе без сохранения, когда
 * завершение было неуспешным и область уже разблокирована); без записей — закрытие панели.
 */
function exitScopedAside(aside, scope) {
    const finisher = BgSaveScope.hasWrites(scope) ? findScopeFinisher(scope) : null;
    if (finisher) {
        BgSaveScope.lock(scope);
        finisher.view.sendFinishCommand(finisher.button, scope);
        return;
    }
    closeAside(aside);
}
/** Кнопка завершения области и view, которому она принадлежит */
function findScopeFinisher(scope) {
    const button = scope.querySelector(FINISH_BUTTON_SELECTOR);
    const viewEl = button?.closest(".im-view");
    if (!button || !viewEl)
        return null;
    const view = Widget.getAll().find(w => {
        const v = w;
        return v.element === viewEl && typeof v.sendFinishCommand === "function";
    });
    return view ? { button, view } : null;
}
/**
 * 2026-10-15: замена содержимого панели с областью фонового сохранения.
 * Ошибка очереди — onCancel, панель и её содержимое остаются (область уже разблокирована).
 * Перед заменой область разблокируется: запрос новой формы может завершиться ошибкой.
 */
async function replaceScopedAside(aside, scope, onConfirm, onCancel) {
    const ok = await BgSaveScope.finish(scope);
    if (!ok) {
        onCancel?.();
        return;
    }
    BgSaveScope.release(scope);
    if (aside.isConnected && hasUnsavedAsideChanges(aside)) {
        confirmAsideClose(aside, onConfirm, onCancel);
        return;
    }
    onConfirm();
}
/** Поддержка подстановки контента в заголовок окна */
function applyDialogTitle(modal) {
    const titleContent = modal.querySelector("[data-dialog-title]");
    if (!titleContent)
        return;
    const asideTitle = modal.querySelector(".modal-aside__title");
    if (!asideTitle)
        return;
    switch (titleContent.dataset.dialogTitle) {
        case 'start':
            asideTitle.innerHTML = asideTitle.innerHTML + ' ' + titleContent.innerHTML;
            break;
        case 'end':
            asideTitle.innerHTML = titleContent.innerHTML + ' ' + asideTitle.innerHTML;
            break;
        default:
            asideTitle.innerHTML = titleContent.innerHTML;
            break;
    }
    titleContent.remove();
}
/** Подключить закрытие к панели (идемпотентно) */
function wireAside(aside) {
    ownCloseControls(aside).forEach(normalizeCloseControl);
    if (wiredAsides.has(aside))
        return;
    wiredAsides.add(aside);
    // Делегирование на панель: переживает замену шапки и содержимого
    aside.addEventListener("click", (e) => {
        const btn = closeControlFromEvent(aside, e);
        if (!btn)
            return;
        requestAsideClose(aside);
    });
    aside.addEventListener("keydown", (e) => {
        const btn = closeControlFromEvent(aside, e);
        if (!btn || btn instanceof HTMLButtonElement)
            return;
        if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar")
            return;
        e.preventDefault();
        requestAsideClose(aside);
    });
}
/** Крестики, принадлежащие именно этой панели (без вложенных панелей) */
function ownCloseControls(aside) {
    return Array.from(aside.querySelectorAll(CLOSE_SELECTOR))
        .filter(btn => btn.closest(ASIDE_SELECTOR) === aside);
}
function closeControlFromEvent(aside, e) {
    const target = e.target;
    const btn = target?.closest(CLOSE_SELECTOR);
    if (!btn || btn.closest(ASIDE_SELECTOR) !== aside)
        return null;
    return btn;
}
/**
 * Legacy-разметка крестика (<i>) получает семантику кнопки без смены тега,
 * чтобы не менять оформление: role, фокус и доступное имя.
 */
function normalizeCloseControl(btn) {
    if (btn instanceof HTMLButtonElement) {
        if (!btn.type)
            btn.type = "button";
    }
    else {
        if (!btn.hasAttribute("role"))
            btn.setAttribute("role", "button");
        if (!btn.hasAttribute("tabindex"))
            btn.tabIndex = 0;
    }
    if (!btn.hasAttribute("aria-label") && !btn.textContent?.trim()) {
        btn.setAttribute("aria-label", Loc.close);
    }
}
/** Панель скрывается, а не удаляется: фильтр таблицы или крестик с onclick-hide */
function isHideMode(aside) {
    return aside.classList.contains("im-modal-aside-filter")
        || ownCloseControls(aside).some(btn => btn.classList.contains("onclick-hide"));
}
/** Закрыть панель без проверок */
function closeAside(aside) {
    if (document.body.classList.contains("player-inited")) {
        ImViewBridge.execEvent(null, "close-vsplayer");
    }
    if (!isHideMode(aside)) {
        aside.remove();
        return;
    }
    // Виджет на самой панели (FilterWidget) закрывается своим close(), чтобы вызвать его callback
    const owner = Widget.allOn(aside).find(w => typeof w.close === "function");
    if (owner) {
        owner.close();
    }
    else {
        DomHelpers.hide(aside);
    }
}
/** Глобальный Escape подключается один раз на документ */
function installEscape() {
    if (escapeInstalled)
        return;
    escapeInstalled = true;
    // Слушатель на document регистрируется при первом initDom, раньше горячих клавиш CmdPanelWidget,
    // и гасит Escape (preventDefault), чтобы тот не отменял редактирование в обход подтверждения.
    document.addEventListener("keydown", onEscape);
}
function onEscape(e) {
    // Escape, уже обработанный вложенным элементом (preventDefault), панели не касается
    if (e.key !== "Escape" || e.defaultPrevented || e.isComposing)
        return;
    // Открыт диалог (в том числе подтверждение закрытия) — Escape принадлежит ему
    if (document.querySelector("im-dialog"))
        return;
    const aside = findTopAside(e.target);
    if (!aside)
        return;
    e.preventDefault();
    // Введённое значение фиксируется в change только при потере фокуса
    const active = document.activeElement;
    if (active instanceof HTMLElement && aside.contains(active) && active.matches("input, textarea, select")) {
        active.blur();
    }
    requestAsideClose(aside);
}
/**
 * Верхняя открытая панель: содержащая источник события, иначе видимая сверху.
 * Порядок в DOM не совпадает с наложением: фильтр таблицы стоит в main раньше карточки из body,
 * но показывается поверх неё, поэтому проверяем, чья панель в своей точке экрана.
 */
function findTopAside(target) {
    const shown = Array.from(document.querySelectorAll(ASIDE_SELECTOR)).filter(isShown);
    if (!shown.length)
        return null;
    const own = target instanceof Element ? target.closest(ASIDE_SELECTOR) : null;
    if (own && shown.includes(own))
        return own;
    const onTop = shown.filter(isOnTop);
    return onTop[onTop.length - 1] ?? shown[shown.length - 1];
}
/** Не перекрыта ли панель в центре своей области */
function isOnTop(aside) {
    if (typeof document.elementFromPoint !== "function")
        return false;
    const r = aside.getBoundingClientRect();
    if (!r.width || !r.height)
        return false;
    const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
    return !!hit && aside.contains(hit);
}
function isShown(el) {
    if (!el.isConnected || el.hidden)
        return false;
    const cs = getComputedStyle(el);
    return cs.display !== "none" && cs.visibility !== "hidden";
}

export { confirmAsideClose, confirmAsideReplace, hasUnsavedAsideChanges, modalAside, requestAsideClose };
